import React, { useState, useEffect } from 'react';
import { Copy, RefreshCw, UserX, Users, AlertCircle, Check, MoreVertical, Eye, UserCheck, Building } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  getEnterpriseDetails, 
  getEnterpriseUsers, 
  regenerateEnterpriseKey,
  revokeEnterpriseUser,
  restoreEnterpriseUser,
  checkEnterpriseExpiration
} from '../services/enterprise';
import EnterpriseRequest from './EnterpriseRequest';

const EnterpriseAdmin = ({ enterpriseId }) => {
  const navigate = useNavigate();
  const [enterprise, setEnterprise] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [revokeUserId, setRevokeUserId] = useState(null);
  const [restoreUserId, setRestoreUserId] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showEnterpriseRequest, setShowEnterpriseRequest] = useState(false);
  
  // Helper function to check if enterprise is expired
const isEnterpriseExpired = (enterprise) => {
  if (!enterprise) return false;
  
  // First check by actual expiration date
  if (enterprise.expiresAt) {
    const expirationDate = new Date(enterprise.expiresAt);
    const currentDate = new Date();
    const isExpiredByDate = currentDate > expirationDate;
    
    // If expired by date, always consider it expired regardless of status
    if (isExpiredByDate) return true;
    
    // If not expired by date, check status
    return enterprise.status !== 'active';
  }
  
  // Fallback to status check if no expiration date
  return enterprise.status !== 'active';
};
  
useEffect(() => {
  const fetchEnterpriseData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [enterpriseData, usersData] = await Promise.all([
        getEnterpriseDetails(enterpriseId),
        getEnterpriseUsers(enterpriseId)
      ]);
      
      setEnterprise(enterpriseData.enterprise);
      setUsers(usersData || []);
      
      // Check if enterprise status needs correction
      if (enterpriseData.enterprise) {
        const currentDate = new Date();
        const expirationDate = new Date(enterpriseData.enterprise.expiresAt);
        const isExpiredByDate = currentDate > expirationDate;
        const currentStatus = enterpriseData.enterprise.status;
        
        // Case 1: Expired by date but status is still 'active'
        if (isExpiredByDate && currentStatus === 'active') {
          console.log('Enterprise has expired by date but status is still active, triggering expiration update...');
          try {
            const response = await checkEnterpriseExpiration(enterpriseId);
            if (response.wasExpired) {
              console.log('Successfully updated enterprise status to expired');
              const updatedEnterpriseData = await getEnterpriseDetails(enterpriseId);
              setEnterprise(updatedEnterpriseData.enterprise);
            }
          } catch (err) {
            console.error('Failed to update enterprise expiration status:', err);
          }
        }
        
        // Case 2: NOT expired by date but status is 'expired' (stuck status)
        else if (!isExpiredByDate && currentStatus === 'expired') {
          console.log('Enterprise is not expired by date but status is stuck at expired, triggering reactivation...');
          try {
            const response = await checkEnterpriseExpiration(enterpriseId);
            if (!response.wasExpired) {
              console.log('Successfully reactivated enterprise status');
              const updatedEnterpriseData = await getEnterpriseDetails(enterpriseId);
              setEnterprise(updatedEnterpriseData.enterprise);
              
              // Also refresh users data since their subscription status may have changed
              const updatedUsersData = await getEnterpriseUsers(enterpriseId);
              setUsers(updatedUsersData || []);
            }
          } catch (err) {
            console.error('Failed to reactivate enterprise status:', err);
          }
        }
      }
      
    } catch (err) {
      console.error('Error fetching enterprise data:', err);
      setError('Failed to load enterprise data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (enterpriseId) {
    fetchEnterpriseData();
  }
}, [enterpriseId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  
  const handleCopyKey = () => {
    if (!enterprise?.enterpriseKey) return;
    
    navigator.clipboard.writeText(enterprise.enterpriseKey);
    setCopiedKey(true);
    
    setTimeout(() => {
      setCopiedKey(false);
    }, 3000);
  };
  
  const handleRegenerateKey = async () => {
    try {
      setRegeneratingKey(true);
      const result = await regenerateEnterpriseKey(enterpriseId);
      
      if (result && result.status === 'success') {
        setEnterprise(prev => ({
          ...prev,
          enterpriseKey: result.enterpriseKey
        }));
      }
      
      setShowRegenerateConfirm(false);
    } catch (err) {
      console.error('Error regenerating key:', err);
      setError('Failed to regenerate enterprise key.');
    } finally {
      setRegeneratingKey(false);
    }
  };
  
  const handleRevokeUser = async (userId) => {
    try {
      const result = await revokeEnterpriseUser(enterpriseId, userId);
      
      if (result && result.status === 'success') {
        // Update user status in the list instead of removing
        setUsers(users.map(user => 
          user.userId === userId 
            ? { ...user, status: 'revoked', revokedAt: new Date().toISOString() }
            : user
        ));
      }
      
      setRevokeUserId(null);
    } catch (err) {
      console.error('Error revoking user:', err);
      setError('Failed to revoke user access.');
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      const result = await restoreEnterpriseUser(enterpriseId, userId);
      
      if (result && result.status === 'success') {
        // Update user status in the list
        setUsers(users.map(user => 
          user.userId === userId 
            ? { ...user, status: 'active', revokedAt: null }
            : user
        ));
      }
      
      setRestoreUserId(null);
    } catch (err) {
      console.error('Error restoring user:', err);
      setError('Failed to restore user access.');
    }
  };

const handleViewUserData = (user) => {
  // Store current scroll position before navigation
  const currentScrollPosition = window.pageYOffset;
  
  navigate('/enterprise/user-data', {
    state: {
      enterpriseId,
      userId: user.userId,
      userEmail: user.email,
      enterpriseName: enterprise?.name,
      scrollPosition: currentScrollPosition
    }
  });
  setActiveDropdown(null);
};

  const toggleDropdown = (e, userId) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === userId ? null : userId);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center text-red-500 mb-4">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{error}</span>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (!enterprise) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <p className="text-gray-600">No enterprise account found.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Enterprise Details Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Users className="mr-2" size={20} />
          Enterprise Account
        </h2>
        
        {/* Expiration Warning Banner */}
        {isEnterpriseExpired(enterprise) && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Enterprise Account Expired
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    Your enterprise account expired on {formatDate(enterprise.expiresAt)}. 
                    Team members can no longer access enterprise features. Please renew your subscription to restore access.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Organization Name</label>
            <p className="mt-1 text-lg font-medium">{enterprise.name}</p>
          </div>
          
          <div className="flex flex-col md:flex-row md:space-x-6 space-y-4 md:space-y-0">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <div className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  isEnterpriseExpired(enterprise) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                }`}>
                  {isEnterpriseExpired(enterprise) ? 'Expired' : 'Active'}
                </span>
              </div>
            </div>
            
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Seat Usage</label>
              <p className="mt-1 text-sm text-gray-900">
                {enterprise.usedSeats} / {enterprise.seatLimit} seats used
              </p>
            </div>
            
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Expires</label>
              <p className="mt-1 text-sm text-gray-900">
                {enterprise.expiresAt ? formatDate(enterprise.expiresAt) : 'Never'}
              </p>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Enterprise Key</label>
            <div className="flex items-center space-x-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 rounded-md text-sm font-mono">
                {enterprise.enterpriseKey}
              </code>
              <button
                onClick={handleCopyKey}
                className="p-2 text-gray-600 hover:text-black transition-colors"
                title="Copy key"
              >
                {copiedKey ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
              </button>
              <button
                onClick={() => setShowRegenerateConfirm(true)}
                className="p-2 text-gray-600 hover:text-black transition-colors"
                title="Regenerate key"
                disabled={regeneratingKey}
              >
                <RefreshCw size={16} className={regeneratingKey ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Users Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Team Members</h2>
        
        {users.length === 0 ? (
          <p className="text-gray-600">No users found.</p>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map(user => (
                  <tr key={user.userId}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {user.status === 'active' ? 'Active' : 'Revoked'}
                      </span>
                      {user.status === 'revoked' && user.revokedAt && (
                        <div className="text-xs text-gray-400 mt-1">
                          Revoked {formatDate(user.revokedAt)}
                        </div>
                      )}
                      {user.status === 'active' && user.joinedAt && (
                        <div className="text-xs text-gray-400 mt-1">
                          Joined {formatDate(user.joinedAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${user.isAdmin ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                        {user.isAdmin ? 'Admin' : 'Member'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!user.isAdmin && (
                        <div className="relative">
                          <button
                            onClick={(e) => toggleDropdown(e, user.userId)}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                          >
                            <MoreVertical size={16} />
                          </button>
                          
                          {activeDropdown === user.userId && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                              <div className="py-1">
                                <button
                                  onClick={() => handleViewUserData(user)}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Data
                                </button>
                                {user.status === 'active' ? (
                                  <button
                                    onClick={() => {
                                      setRevokeUserId(user.userId);
                                      setActiveDropdown(null);
                                    }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                  >
                                    <UserX className="w-4 h-4 mr-2" />
                                    Revoke Access
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setRestoreUserId(user.userId);
                                      setActiveDropdown(null);
                                    }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-green-600 hover:bg-gray-100"
                                  >
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Restore Access
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Renew Enterprise Account Card - Only shown if expired */}
      {isEnterpriseExpired(enterprise) && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Building className="mr-2" size={20} />
            Renew Enterprise Account
          </h2>

          <p className="text-gray-600 mb-4">
            Your enterprise account has expired. Renew your subscription to restore access for all team members and continue managing your organization's account.
          </p>

          <button
            onClick={() => navigate('/enterprise/create', {
              state: {
                isRenewal: true,
                organizationName: enterprise.name,
                // --- FIX: Pass the unique enterpriseId ---
                enterpriseId: enterpriseId 
              }
            })}
            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
          >
            Renew Enterprise Account
          </button>
        </div>
      )}

      {/* Contact Us Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Contact us</h2>
        <p className="text-gray-600 mb-4">
          Please contact us for your requests such as changing admin user and deleting user data.
        </p>
        <button
          onClick={() => setShowEnterpriseRequest(true)}
          className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
        >
          Contact us
        </button>
      </div>

      {/* Regenerate Key Confirmation Modal */}
      {showRegenerateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Regenerate Enterprise Key</h3>
            <p className="text-gray-600 mb-6">
              This will generate a new enterprise key. The old key will no longer work for new invitations.
              Are you sure you want to continue?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowRegenerateConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerateKey}
                disabled={regeneratingKey}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {regeneratingKey ? 'Regenerating...' : 'Regenerate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke User Confirmation Modal */}
      {revokeUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Revoke User Access</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to revoke access for this user? They will lose access to all enterprise features immediately.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setRevokeUserId(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRevokeUser(revokeUserId)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Revoke Access
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore User Confirmation Modal */}
      {restoreUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Restore User Access</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to restore access for this user? They will regain access to all enterprise features.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setRestoreUserId(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRestoreUser(restoreUserId)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Restore Access
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enterprise Request Modal */}
      {showEnterpriseRequest && (
        <EnterpriseRequest 
          isOpen={showEnterpriseRequest}
          onClose={() => setShowEnterpriseRequest(false)}
        />
      )}
    </div>
  );
};

export default EnterpriseAdmin;